/**
 * Scan Configuration (v5 - Seamless UX)
 * 
 * Centralized configuration for the Scan & Read feature.
 * 
 * v5 Philosophy:
 * - INSTRUCTIONS FIRST: Show clear instructions before camera activates
 * - CALM SCANNING: Minimal visual noise - user is looking at BOOK, not screen
 * - DEFERRED FEEDBACK: Quality issues shown AFTER scan completes, not during
 * 
 * All magic numbers and timing values should be defined here.
 */

// ============================================================================
// Scanner Timing Configuration
// ============================================================================

export const SCANNER_CONFIG = {
  /**
   * Document Detection
   */
  // Minimum confidence (0-100) for Dynamsoft to consider a document detected
  // Lowered from 70 to let AI do precise boundary detection
  // v4.1: Could lower further since we generate Turn illustrations (not display original)
  // but keeping at 60 for good UX (captures at right moment)
  CONFIDENCE_THRESHOLD: 60,
  
  // Number of consecutive stable frames required before auto-capture
  // 3 frames at ~100ms = 300ms stable hold
  STABLE_FRAMES_REQUIRED: 3,
  
  // Interval (ms) between detection frame processing
  DETECTION_INTERVAL_MS: 100,
  
  /**
   * Capture Cooldown (2-phase system)
   */
  // Phase 1: "Captured!" success flash duration
  PHASE_1_DURATION_MS: 800,
  
  // Phase 2: "Turn page now" cooldown duration
  PHASE_2_DURATION_MS: 1500,
  
  /**
   * Camera Settings
   */
  // Target camera resolution
  CAMERA_WIDTH: 1920,
  CAMERA_HEIGHT: 1080,
  
  // JPEG quality for captured images (0-100)
  CAPTURE_JPEG_QUALITY: 0.90,
  DESKEW_JPEG_QUALITY: 0.92,
} as const;

// ============================================================================
// OCR Processing Configuration
// ============================================================================

export const OCR_CONFIG = {
  /**
   * @deprecated v4.1: Deskewing removed from pipeline
   * GPT-4o Vision handles skewed input fine. Kept for reference.
   */
  // Time to wait (ms) for deskewed image before processing with raw only
  DESKEW_WAIT_MS: 5000,
  
  /**
   * Rate Limiting
   */
  // Number of pages to process concurrently
  BATCH_SIZE: 2,
  
  // Delay (ms) between batch processing
  BATCH_DELAY_MS: 500,
  
  /**
   * Retry Configuration
   */
  // Max retries for API calls
  MAX_RETRIES: 3,
  
  // Initial delay (ms) before first retry
  INITIAL_RETRY_DELAY_MS: 2000,
  
  // Maximum delay (ms) between retries
  MAX_RETRY_DELAY_MS: 30000,
  
  // Backoff multiplier for exponential backoff
  BACKOFF_MULTIPLIER: 2,
  
  /**
   * Validation
   */
  // Max retries for validation API
  VALIDATION_MAX_RETRIES: 2,
  VALIDATION_INITIAL_DELAY_MS: 1000,
  VALIDATION_MAX_DELAY_MS: 10000,
  
  // Max retries for enhancement API
  ENHANCE_MAX_RETRIES: 2,
  ENHANCE_INITIAL_DELAY_MS: 1000,
  ENHANCE_MAX_DELAY_MS: 10000,
} as const;

// ============================================================================
// Bounds Validation Configuration
// ============================================================================

export const BOUNDS_CONFIG = {
  /**
   * Minimum dimensions for valid page bounds
   * Relaxed for children's books which can have narrow text strips or wide panoramic captures
   */
  // Minimum width as fraction of image width (15% allows narrow text areas)
  MIN_WIDTH: 0.15,
  
  // Minimum height as fraction of image height
  MIN_HEIGHT: 0.15,
  
  // Minimum area as fraction of total image (10% for panoramic two-page spreads)
  MIN_AREA: 0.10,
  
  // Overflow tolerance for rounding errors
  OVERFLOW_TOLERANCE: 0.05,
  
  /**
   * Enhancement padding
   */
  // Default padding around detected bounds
  DEFAULT_PADDING: 0.02,
  
  // Padding for illustration-only extraction
  ILLUSTRATION_PADDING: 0.01,
} as const;

// ============================================================================
// Image Quality Settings
// ============================================================================

export const IMAGE_CONFIG = {
  /**
   * Enhancement Settings
   */
  // Default JPEG quality for enhanced images
  ENHANCE_JPEG_QUALITY: 90,
  
  // Maximum dimension for enhanced images
  MAX_DIMENSION: 2000,
  
  /**
   * Context for OCR
   */
  // Number of characters from previous page to include as context
  PREVIOUS_PAGE_CONTEXT_CHARS: 200,
} as const;

// ============================================================================
// UI Configuration
// ============================================================================

export const UI_CONFIG = {
  /**
   * Page Dots Display
   */
  // Maximum number of page dots to show in header
  MAX_PAGE_DOTS: 20,
  
  /**
   * Rescan Review
   */
  // Maximum pages to show in rescan review before "show more"
  MAX_RESCAN_PAGES_VISIBLE: 10,
  
  /**
   * Auto-proceed Delays
   */
  // Delay (ms) before auto-proceeding after successful ISBN scan
  ISBN_SUCCESS_AUTO_PROCEED_MS: 2000,
} as const;

// ============================================================================
// Board Book Size Presets
// ============================================================================

export interface BookSizePreset {
  id: string;
  name: string;
  description: string;
  /** Approximate aspect ratio (width/height) */
  aspectRatio: number;
  /** Minimum confidence threshold for this book type */
  confidenceThreshold: number;
  /** Minimum area as fraction of camera frame */
  minArea: number;
  /** Whether to use panoramic mode for two-page spreads */
  supportsTwoPageSpread: boolean;
}

export const BOOK_SIZE_PRESETS: BookSizePreset[] = [
  {
    id: 'standard-board',
    name: 'Standard Board Book',
    description: '7"×7" - Most common board books',
    aspectRatio: 1.0,
    confidenceThreshold: 60,
    minArea: 0.15,
    supportsTwoPageSpread: true,
  },
  {
    id: 'mini-board',
    name: 'Mini Board Book',
    description: '4"×4" - Small novelty books',
    aspectRatio: 1.0,
    confidenceThreshold: 50, // Lower threshold for small books
    minArea: 0.08, // Smaller area requirement
    supportsTwoPageSpread: false,
  },
  {
    id: 'tall-board',
    name: 'Tall Board Book',
    description: '5"×8" - Taller board books',
    aspectRatio: 0.625,
    confidenceThreshold: 55,
    minArea: 0.12,
    supportsTwoPageSpread: true,
  },
  {
    id: 'picture-book',
    name: 'Picture Book',
    description: '10"×10" - Standard picture books',
    aspectRatio: 1.0,
    confidenceThreshold: 60,
    minArea: 0.20,
    supportsTwoPageSpread: true,
  },
  {
    id: 'large-picture',
    name: 'Large Picture Book',
    description: '12"×12" - Oversized picture books',
    aspectRatio: 1.0,
    confidenceThreshold: 65,
    minArea: 0.25,
    supportsTwoPageSpread: true,
  },
  {
    id: 'panoramic',
    name: 'Panoramic/Landscape',
    description: '14"×8" - Wide landscape format',
    aspectRatio: 1.75,
    confidenceThreshold: 55,
    minArea: 0.15,
    supportsTwoPageSpread: false, // Already wide
  },
  {
    id: 'auto',
    name: 'Auto Detect',
    description: 'Let AI determine book size',
    aspectRatio: 1.0,
    confidenceThreshold: SCANNER_CONFIG.CONFIDENCE_THRESHOLD,
    minArea: BOUNDS_CONFIG.MIN_AREA,
    supportsTwoPageSpread: true,
  },
];

/** Get preset by ID, falls back to 'auto' */
export function getBookSizePreset(presetId: string): BookSizePreset {
  return BOOK_SIZE_PRESETS.find(p => p.id === presetId) || BOOK_SIZE_PRESETS.find(p => p.id === 'auto')!;
}

/** Default preset */
export const DEFAULT_BOOK_SIZE_PRESET = 'auto';

// ============================================================================
// v4.0 Multi-Capture Buffer Configuration
// ============================================================================

export const CAPTURE_BUFFER_CONFIG = {
  /** Maximum captures to keep per page slot */
  MAX_CAPTURES_PER_PAGE: 3,
  
  /** Minimum time (ms) between captures on same page to prevent accidental duplicates */
  MIN_CAPTURE_INTERVAL_MS: 500,
  
  /** Time (ms) to wait for validation before showing timeout status */
  VALIDATION_TIMEOUT_MS: 15000,
  
  /** Maximum concurrent validation requests */
  MAX_CONCURRENT_VALIDATIONS: 2,
  
  /** Time (ms) to show success feedback before allowing next capture */
  CAPTURE_FEEDBACK_MS: 300,
} as const;

// ============================================================================
// v4.0 Multi-Capture Buffer Types
// ============================================================================

/** Validation status for a single capture */
export type CaptureValidationStatus = 
  | 'pending'      // Waiting for validation
  | 'validating'   // Validation in progress
  | 'excellent'    // High quality, use as-is
  | 'good'         // Acceptable quality
  | 'fair'         // Usable but not ideal
  | 'poor'         // Low quality, prefer another
  | 'invalid'      // Unusable, needs recapture
  | 'error';       // Validation failed

/** A single capture attempt for a page */
export interface PageCapture {
  /** Unique ID for this capture */
  id: string;
  
  /** Page number this capture is for */
  pageNumber: number;
  
  /** Raw captured image (base64) */
  rawImage: string;
  
  /** Deskewed image if available (base64) */
  deskewedImage?: string;
  
  /** Dynamsoft confidence score (0-100) */
  dynamsoftConfidence: number;
  
  /** Page area as fraction of frame (0-1) */
  pageArea: number;
  
  /** Current validation status */
  validationStatus: CaptureValidationStatus;
  
  /** Detailed validation result from AI */
  validationResult?: {
    isValidCapture: boolean;
    captureQuality: 'excellent' | 'good' | 'fair' | 'poor' | 'invalid';
    issues: string[];
    rejectionReason: string | null;
  };
  
  /** When this capture was taken */
  capturedAt: number;
  
  /** Book ID at capture time (prevents stale closure bugs) */
  bookId: string;
  
  /** Book title at capture time */
  bookTitle: string;
}

/** Buffer of captures for a single page slot */
export interface PageCaptureBuffer {
  /** Page number */
  pageNumber: number;
  
  /** All captures for this page (max MAX_CAPTURES_PER_PAGE) */
  captures: PageCapture[];
  
  /** Index of best capture (null if none validated yet) */
  bestCaptureIndex: number | null;
  
  /** Overall page status derived from best capture */
  pageStatus: 'empty' | 'capturing' | 'validating' | 'valid' | 'needs_attention' | 'invalid';
  
  /** Whether OCR has been started for this page */
  ocrStarted: boolean;
  
  /** Whether OCR has completed for this page */
  ocrCompleted: boolean;
}

/** The complete capture buffer for a scanning session */
export interface CaptureBufferState {
  /** Map of page number to capture buffer */
  pages: Map<number, PageCaptureBuffer>;
  
  /** Queue of captures waiting for validation */
  validationQueue: string[];  // capture IDs
  
  /** Currently validating capture IDs */
  validatingNow: Set<string>;
  
  /** Total captures taken this session */
  totalCaptures: number;
  
  /** Captures that failed validation */
  failedCaptures: number;
}

/** Create an empty capture buffer state */
export function createEmptyCaptureBuffer(): CaptureBufferState {
  return {
    pages: new Map(),
    validationQueue: [],
    validatingNow: new Set(),
    totalCaptures: 0,
    failedCaptures: 0,
  };
}

/** Create an empty page buffer */
export function createEmptyPageBuffer(pageNumber: number): PageCaptureBuffer {
  return {
    pageNumber,
    captures: [],
    bestCaptureIndex: null,
    pageStatus: 'empty',
    ocrStarted: false,
    ocrCompleted: false,
  };
}

/** Generate a unique capture ID */
export function generateCaptureId(): string {
  return `cap_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Best-shot selection algorithm
 * 
 * Priority order:
 * 1. excellent > good > fair > poor (validation quality)
 * 2. Higher Dynamsoft confidence (within same quality tier)
 * 3. Larger page area (within same confidence tier)
 * 4. More recent capture (tiebreaker)
 */
export function selectBestCapture(buffer: PageCaptureBuffer): number | null {
  if (buffer.captures.length === 0) return null;
  
  const qualityScore: Record<CaptureValidationStatus, number> = {
    excellent: 100,
    good: 80,
    fair: 60,
    poor: 40,
    pending: 30,
    validating: 30,
    invalid: 0,
    error: 0,
  };
  
  let bestIndex = 0;
  let bestScore = -1;
  
  buffer.captures.forEach((capture, index) => {
    // Skip invalid/error captures entirely
    if (capture.validationStatus === 'invalid' || capture.validationStatus === 'error') {
      return;
    }
    
    // Composite score: quality * 1000 + confidence * 10 + area * 100
    const score = 
      qualityScore[capture.validationStatus] * 1000 +
      capture.dynamsoftConfidence * 10 +
      capture.pageArea * 100;
    
    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });
  
  return bestScore > 0 ? bestIndex : null;
}

/**
 * Derive page status from captures
 */
export function derivePageStatus(buffer: PageCaptureBuffer): PageCaptureBuffer['pageStatus'] {
  if (buffer.captures.length === 0) return 'empty';
  
  const hasExcellent = buffer.captures.some(c => c.validationStatus === 'excellent');
  const hasGood = buffer.captures.some(c => c.validationStatus === 'good');
  const hasFair = buffer.captures.some(c => c.validationStatus === 'fair');
  const hasValidating = buffer.captures.some(c => c.validationStatus === 'validating' || c.validationStatus === 'pending');
  const allInvalid = buffer.captures.every(c => c.validationStatus === 'invalid' || c.validationStatus === 'error');
  
  if (hasExcellent || hasGood) return 'valid';
  if (hasFair) return 'valid'; // Fair is usable
  if (hasValidating) return 'validating';
  if (allInvalid) return 'invalid';
  
  return 'needs_attention';
}

// ============================================================================
// ISBN Barcode Scanner Configuration
// ============================================================================

/**
 * html5-qrcode API Notes:
 * 
 * scanner.start(cameraIdOrConfig, scanConfig, onSuccess, onError)
 * 
 * - cameraIdOrConfig: MUST be either a deviceId string OR simple object with ONE key
 *   e.g., { facingMode: 'environment' } - NOT full MediaTrackConstraints
 * 
 * - scanConfig.videoConstraints: This is where MediaTrackConstraints go for HD resolution
 *   The videoConstraints override cameraIdOrConfig when provided
 * 
 * CRITICAL FACTORS FOR BARCODE DETECTION DISTANCE:
 * 1. Resolution - Higher = more pixels per barcode bar
 * 2. Focus - Continuous autofocus must be enabled
 * 3. Scan area - Full frame scanning (no qrbox restriction)
 */
export const ISBN_SCANNER_CONFIG = {
  /**
   * Camera Selection (first parameter to scanner.start)
   * Simple object with ONE key - html5-qrcode requirement
   */
  CAMERA_CONFIG: { facingMode: 'environment' } as const,
  
  /**
   * Video Constraints for HD Resolution (goes in scanConfig.videoConstraints)
   * 
   * Resolution affects detection distance - more pixels = detectable from further.
   * Default getUserMedia often returns 640x480 which requires getting very close.
   */
  VIDEO_CONSTRAINTS: {
    width: { ideal: 1920, min: 1280 },
    height: { ideal: 1080, min: 720 },
    facingMode: 'environment',
    frameRate: { ideal: 30, min: 15 },
  } as MediaTrackConstraints,
  
  /**
   * Focus Constraints - CRITICAL for detection at distance
   * 
   * Applied AFTER scanner starts via applyVideoConstraints().
   * Continuous autofocus keeps the barcode sharp as user moves camera.
   * Without this, camera may hunt for focus or lock on wrong distance.
   * 
   * Note: Not all devices support focusMode - check capabilities first.
   */
  FOCUS_CONSTRAINTS: {
    // 'continuous' = camera constantly adjusts focus (best for scanning)
    // 'manual' = fixed focus distance (useful if you know exact distance)
    // 'single-shot' = focus once then lock
    focusMode: 'continuous',
  } as MediaTrackConstraints,
  
  // Frames per second for barcode PROCESSING (not camera fps)
  // Higher = more detection attempts per second
  // 10 fps = 100ms per frame, good balance of speed vs CPU
  FPS: 10,
  
  // Aspect ratio for camera preview (matches HD video)
  ASPECT_RATIO: 1.777, // 16:9
  
  /**
   * Supported Barcode Formats
   * EAN-13 is standard ISBN, others for older books
   */
  SUPPORTED_FORMATS: ['EAN_13', 'EAN_8', 'UPC_A', 'UPC_E'] as const,
  
  /**
   * Timing
   */
  // Delay (ms) before initializing scanner (DOM ready)
  INIT_DELAY_MS: 150,
} as const;

// ============================================================================
// Type Exports
// ============================================================================

export type ScannerConfig = typeof SCANNER_CONFIG;
export type OcrConfig = typeof OCR_CONFIG;
export type BoundsConfig = typeof BOUNDS_CONFIG;
export type ImageConfig = typeof IMAGE_CONFIG;
export type UiConfig = typeof UI_CONFIG;
export type CaptureBufferConfig = typeof CAPTURE_BUFFER_CONFIG;
export type IsbnScannerConfig = typeof ISBN_SCANNER_CONFIG;
